import { toast } from 'react-toastify';

import { tracking } from 'app/services/tracking';
import { trackUpgradeStarted } from 'app/services/tracking/payments/trackEvents';

import { getSubscriptionPlans } from '.';
import { NewTierPlan } from './getNewTierPlans';
import { mapOfferToSubscriptionPlan, SelectableOffer } from './mapOfferToSubscriptionPlan';
import { CurrentTier } from './resolveCurrentTier';

export type { SelectableOffer };

export type ProductSelected = {
  id: number | string;
  plan_type: string;
  name: string;
  description: string;
  periodicity: string;
  price: number;
  gateway_id: string | null;
};

// isActive(subscriptionStatus) ? 'upgrade' : 'reactivation'; legado ativo
// (sem plano atual resolvível no catálogo) ainda conta como upgrade.
export type UpgradeTrackingMode = 'upgrade' | 'reactivation';

// propriedades novas exigidas pelo ticket em todos os eventos do fluxo de
// upgrade/reativação autenticado (trackUpgradeStarted/trackPlanUpgraded/trackUpgradePaymentSuccess-Failed):
// catálogo, convênio, tier atual, tier escolhido e modo. Computadas uma vez na seleção do plano e
// carregadas até o checkout via formik (upgrade_tracking_properties), nunca dentro de ProductSelected
// (esse é o payload literal enviado ao POST /subscription/create).
export type UpgradeTrackingProperties = {
  mode: UpgradeTrackingMode;
  current_tier: CurrentTier | null;
  new_tier: NewTierPlan['key'];
  catalog_offer_id: number | string | null;
  coupon_code: string | null;
};

export const buildUpgradeTrackingProperties = ({
  mode,
  currentTier,
  planKey,
  offer,
}: {
  mode: UpgradeTrackingMode;
  currentTier?: CurrentTier;
  planKey: NewTierPlan['key'];
  offer?: SelectableOffer;
}): UpgradeTrackingProperties => ({
  mode,
  current_tier: currentTier ?? null,
  new_tier: planKey,
  catalog_offer_id: offer?.catalogOfferId ?? null,
  coupon_code: offer?.couponCode ?? null,
});

export type SelectNewTierPlanParams = {
  planKey: NewTierPlan['key'];
  // quando informado, o checkout usa os dados já resolvidos pelo catálogo (API)
  // em vez de reconsultar getSubscriptionPlans, que é sempre hardcoded.
  offer?: SelectableOffer;
  formik: { setFieldValue: (field: string, value: unknown) => void };
  selectPlan: () => void;
  user: unknown;
  subscriptionStatus: unknown;
  url: string;
  mode: UpgradeTrackingMode;
  // tier atual do cliente (useSubscriptionStatus().currentTier); ausente nas
  // ~15 chamadas legadas fora do escopo desta tarefa (ver brief).
  currentTier?: CurrentTier;
};

// Extracted from ChoosePlan.js:chooseProduct (isNewPricingFlow branch) — reused by
// legacy ChoosePlan and ExplorePlansModal. 2026 plans are monthly-only, without
// JusProcessos/affiliation.
export const selectNewTierPlan = ({
  planKey,
  offer,
  formik,
  selectPlan,
  user,
  subscriptionStatus,
  url,
  mode,
  currentTier,
}: SelectNewTierPlanParams): boolean => {
  const subscriptionPlan = offer
    ? mapOfferToSubscriptionPlan(offer, planKey)
    : getSubscriptionPlans({
        plan_type: planKey,
        billingCycle: 'monthly',
        is_offer: false,
      });

  const hasValidId =
    typeof subscriptionPlan?.id === 'number' ||
    typeof subscriptionPlan?.id === 'string';

  if (!subscriptionPlan || !hasValidId || subscriptionPlan.gateway_id == null) {
    toast.error('Plano não encontrado. Por favor, tente novamente ou contate o suporte.');
    formik.setFieldValue('product_selected', null);
    return false;
  }

  const productSelected: ProductSelected = {
    id: subscriptionPlan.id,
    plan_type: subscriptionPlan.plan_type,
    name: subscriptionPlan.name,
    description: subscriptionPlan.description,
    periodicity: subscriptionPlan.billingCycle,
    price: subscriptionPlan.amount,
    gateway_id: String(subscriptionPlan.gateway_id),
  };

  formik.setFieldValue('product_selected', productSelected);

  // Não grava upgrade_tracking_properties no formik do chamador aqui: os call
  // sites com formik real (PlanUpgradeModal/ChoosePlan) submetem `values` inteiro ao backend, e esse
  // campo não faz parte do contrato de POST /subscription/create. Quem precisa persistir essas
  // propriedades (useHandleSelectNewTierPlan, único consumidor com o formik local isolado do
  // PlanExplorationModal) grava explicitamente depois de um select bem-sucedido.
  const trackingProperties = buildUpgradeTrackingProperties({ mode, currentTier, planKey, offer });

  const upgradeStartedProps = tracking.upgrade.prepareUpgradeStartedForAnalytics(
    user,
    subscriptionStatus,
    productSelected,
    { status: 'success', type: 'card' },
    url,
  );
  trackUpgradeStarted({ ...upgradeStartedProps, ...trackingProperties });

  selectPlan();

  return true;
};
